import React, { useRef, useState } from "react";
import Swal from "sweetalert2";
import "sweetalert2/src/sweetalert2.scss";

const ACCEPT = ".pdf,.xls,.xlsx,.csv";
const MAX_SIZE = 10 * 1024 * 1024;

const errorText = (error) => error.response?.data?.message || "Upload failed.";

// One documents column of one row: the files it holds, and an upload button while there is room.
// onBusy(true/false) tells the screen an upload is running, so it can hold back its submit button.
// Removing a file deletes it at once: from storage, from the documents list and from the record.
function DocumentsCell({ company, files, maxFiles = 1, onChange, onBusy = () => {}, alert }) {
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState("");

  const list = Array.isArray(files) ? files : [];
  const room = maxFiles - list.length;

  const upload = async (event) => {
    const chosen = Array.from(event.target.files || []);
    event.target.value = "";
    if (!chosen.length) return;

    if (chosen.length > room) {
      alert.show(`This column takes ${maxFiles} file${maxFiles === 1 ? "" : "s"}; ${room} more can be added.`, { type: "error" });
      return;
    }
    const tooBig = chosen.find((file) => file.size > MAX_SIZE);
    if (tooBig) {
      alert.show(`${tooBig.name} is larger than 10 MB.`, { type: "error" });
      return;
    }

    const form = new FormData();
    form.append("company", company);
    chosen.forEach((file) => form.append("files", file));

    setUploading(true);
    onBusy(true);
    try {
      const response = await window.axios.post("/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...list, ...response.data.map(({ _id, originalName, url }) => ({ _id, originalName, url }))]);
    } catch (error) {
      alert.show(errorText(error), { type: "error" });
    } finally {
      setUploading(false);
      onBusy(false);
    }
  };

  const remove = async (file) => {
    if (removing) return;

    const result = await Swal.fire({
      title: "Remove file?",
      text: `${file.originalName} is deleted permanently. This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, remove",
    });
    if (!result.isConfirmed) return;

    setRemoving(file._id);
    try {
      await window.axios.delete(`/documents/${file._id}`);
      onChange(list.filter((item) => item._id !== file._id));
    } catch (error) {
      // already gone on the server: drop it from the list as well
      if (error.response?.status === 404) onChange(list.filter((item) => item._id !== file._id));
      else alert.show(error.response?.data?.message || "Could not remove the file.", { type: "error" });
    } finally {
      setRemoving("");
    }
  };

  return (
    <div style={{ minWidth: 180 }}>
      {list.map((file) => (
        <div key={file._id} className="d-flex align-items-center mb-1">
          <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-truncate" style={{ maxWidth: 150 }} title={file.originalName}>
            {file.originalName}
          </a>
          <span
            role="button"
            aria-label={`Remove ${file.originalName}`}
            title="Remove"
            className="text-danger ml-2"
            style={{ cursor: "pointer" }}
            onClick={() => remove(file)}
          >
            &times;
          </span>
        </div>
      ))}

      {room > 0 && (
        <>
          <input ref={input} type="file" accept={ACCEPT} multiple={room > 1} style={{ display: "none" }} onChange={upload} />
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={!company || uploading} onClick={() => input.current.click()}>
            {uploading ? "Uploading..." : `Upload (${list.length}/${maxFiles})`}
          </button>
          {!company && <div className="small text-muted">Select a company first.</div>}
        </>
      )}
    </div>
  );
}

export default DocumentsCell;
